import React, { useState } from 'react';
import styled from 'styled-components';
import SharedPageLayout from './SharedPageLayout';
import { theme } from '../theme';
import SocialMediaLinks from './shared/SocialMediaLinks';
import ContactEmail from './shared/ContactEmail';

const ContactMainSection = styled.section`
  width: 100%;
  padding: 80px 5%;

  @media (max-width: 768px) {
    padding: 60px 4%;
  }

  @media (max-width: 480px) {
    padding: 40px 3%;
  }
`;

const ContactMainContent = styled.div`
  max-width: 1200px;
  text-align: justify;
  margin: 0 auto;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 80px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 48px;
  }

  @media (max-width: 480px) {
    gap: 32px;
  }
`;

const ContactFormSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 32px;

  @media (max-width: 768px) {
    gap: 24px;
  }

  @media (max-width: 480px) {
    gap: 20px;
  }
`;

const FormTitle = styled.h2`
  font-family: ${theme.fonts.title};
  font-weight: 600;
  font-size: 32px;
  line-height: 40px;
  color: ${theme.colors.outchou_blue};
  margin: 0;

  @media (max-width: 768px) {
    font-size: 28px;
    line-height: 36px;
  }

  @media (max-width: 480px) {
    font-size: 24px;
    line-height: 32px;
  }
`;

const FormDescription = styled.p`
  font-family: ${theme.fonts.body};
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: ${theme.colors.gray};
  margin: 0;

  @media (max-width: 480px) {
    font-size: 14px;
    line-height: 21px;
  }
`;

const ContactForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (max-width: 768px) {
    gap: 20px;
  }

  @media (max-width: 480px) {
    gap: 16px;
  }
`;

const FormRow = styled.div`
  display: flex;
  gap: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
  }

  @media (max-width: 480px) {
    gap: 16px;
  }
`;

const FormGroup = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FormLabel = styled.label`
  font-family: ${theme.fonts.body};
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: ${theme.colors.outchou_blue};
`;

const FormInput = styled.input`
  padding: 16px;
  border: 1px solid #E0E0E0;
  border-radius: 8px;
  font-family: ${theme.fonts.body};
  font-size: 16px;
  line-height: 24px;
  color: ${theme.colors.outchou_blue};
  background: ${theme.colors.white};
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.outchou_green};
  }

  &::placeholder {
    color: #999999;
  }

  @media (max-width: 480px) {
    padding: 14px;
    font-size: 14px;
  }
`;

const FormTextarea = styled.textarea`
  padding: 16px;
  border: 1px solid #E0E0E0;
  border-radius: 8px;
  font-family: ${theme.fonts.body};
  font-size: 16px;
  line-height: 24px;
  color: ${theme.colors.outchou_blue};
  background: ${theme.colors.white};
  resize: vertical;
  min-height: 120px;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.outchou_green};
  }

  &::placeholder {
    color: #999999;
  }

  @media (max-width: 480px) {
    padding: 14px;
    font-size: 14px;
    min-height: 100px;
  }
`;

const SubmitButton = styled.button`
  background: ${theme.colors.outchou_green};
  border: none;
  border-radius: 8px;
  padding: 16px 32px;
  font-family: ${theme.fonts.body};
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: ${theme.colors.white};
  cursor: pointer;
  transition: background-color 0.3s ease;
  align-self: flex-start;

  &:hover {
    background: ${theme.colors.outchou_green_hover};
  }

  @media (max-width: 480px) {
    padding: 14px 24px;
    font-size: 14px;
    width: 100%;
  }
`;

const ContactSection = styled.section`
  width: 100%;
  padding: 64px 5%;
  background: ${theme.colors.light_beige};

  @media (max-width: 768px) {
    padding: 48px 4%;
  }

  @media (max-width: 480px) {
    padding: 32px 3%;
  }
`;

const ContactContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 32px;

  @media (max-width: 768px) {
    gap: 24px;
  }

  @media (max-width: 480px) {
    gap: 20px;
  }
`;

const ContactTextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  flex: 1;

  @media (max-width: 768px) {
    gap: 24px;
  }

  @media (max-width: 480px) {
    gap: 20px;
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: 480px) {
    gap: 12px;
  }
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 480px) {
    gap: 8px;
  }
`;

const ContactIcon = styled.img`
  width: 24px;
  height: 24px;

  @media (max-width: 480px) {
    width: 20px;
    height: 20px;
  }
`;

const ContactText = styled.p`
  font-family: ${theme.fonts.body};
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 100%;
  color: ${theme.colors.outchou_blue};

  @media (max-width: 768px) {
    font-size: 14px;
  }

  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const ContactAccroche = styled.p`
  font-family: ${theme.fonts.body};
  font-weight: 500;
  font-size: 18px;
  line-height: 26px;
  color: ${theme.colors.outchou_blue};
  margin: 0;
  /* font-style: italic; */

  @media (max-width: 480px) {
    font-size: 16px;
    line-height: 24px;
  }
`;

const ContactInfoSection = styled.div`
  width: 400px;
  display: flex;
  flex-direction: column;
  gap: 48px;

  @media (max-width: 768px) {
    width: 100%;
    gap: 32px;
  }

  @media (max-width: 480px) {
    gap: 24px;
  }
`;

const ContactInfoTitle = styled.h3`
  font-family: ${theme.fonts.title};
  font-weight: 600;
  font-size: 24px;
  line-height: 32px;
  color: ${theme.colors.outchou_blue};
  margin: 0;

  @media (max-width: 768px) {
    font-size: 20px;
    line-height: 28px;
  }

  @media (max-width: 480px) {
    font-size: 18px;
    line-height: 26px;
  }
`;

const ContactInfoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (max-width: 768px) {
    gap: 20px;
  }

  @media (max-width: 480px) {
    gap: 16px;
  }
`;

const ContactInfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;

  @media (max-width: 480px) {
    gap: 12px;
  }
`;

const ContactInfoIcon = styled.img`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  margin-top: 2px;

  @media (max-width: 480px) {
    width: 20px;
    height: 20px;
  }
`;

const ContactInfoText = styled.p`
  font-family: ${theme.fonts.body};
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: ${theme.colors.outchou_blue};
  margin: 0;

  @media (max-width: 480px) {
    font-size: 14px;
    line-height: 21px;
  }
`;

const SocialSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SocialText = styled.span`
  font-family: ${theme.fonts.body};
  font-weight: 500;
  font-size: 16px;
  color: ${theme.colors.gray};
`;


const ContactButton = styled.button`
  background: ${theme.colors.outchou_green};
  border: none;
  border-radius: 8px;
  padding: 16px 32px;
  font-family: ${theme.fonts.body};
  font-weight: 600;
  font-size: 16px;
  color: white;
  cursor: pointer;
  transition: background-color 0.3s ease;
  align-self: flex-start;

  &:hover {
    background: ${theme.colors.outchou_green_hover};
  }

  @media (max-width: 480px) {
    padding: 12px 24px;
    font-size: 14px;
  }
  color: #FFFFFF;

  @media (max-width: 768px) {
    width: 160px;
    height: 52px;
    font-size: 18px;
  }

  @media (max-width: 480px) {
    width: 140px;
    height: 48px;
    font-size: 16px;
  }
`;

const ContactImage = styled.div`
  width: 100%;
  height: 300px;
          background-image: url('/shared_assets/images/contact.jpeg');
  background-size: cover;
  background-position: center;
  border-radius: 16px;

  @media (max-width: 768px) {
    height: 250px;
  }

  @media (max-width: 480px) {
    height: 200px;
  }
`;

const Notification = styled.div<{ $show: boolean; $type: 'success' | 'error' }>`
  position: fixed;
  top: 100px;
  left: 50%;
  transform: translateX(-50%) translateY(${props => props.$show ? '0' : '-100px'});
  background: ${props => props.$type === 'success' ? theme.colors.outchou_green : '#dc3545'};
  color: white;
  padding: 16px 32px;
  border-radius: 8px;
  font-family: ${theme.fonts.body};
  font-size: 16px;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  opacity: ${props => props.$show ? 1 : 0};
  transition: all 0.3s ease;
  text-align: center;

  @media (max-width: 480px) {
    padding: 12px 24px;
    font-size: 14px;
    width: 90%;
    max-width: 320px;
  }
`;

const ContactPage: React.FC = () => {
  const [showNotification, setShowNotification] = useState(false);
  const [notificationType, setNotificationType] = useState<'success' | 'error'>('success');
  const [notificationMessage, setNotificationMessage] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const { firstName, lastName, email, subject, message } = formData;
    const contactEmail = 'contact@outchou.com';
    
    // Construire le corps du message
    const body = `Bonjour,

${message}

---
Informations de contact :
Nom : ${firstName} ${lastName}
Email : ${email}`;
    
    // Créer le lien mailto
    const mailtoLink = `mailto:${contactEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    
    // Ouvrir le client email
    window.location.href = mailtoLink;
    
    // Afficher la notification de succès
    setNotificationType('success');
    setNotificationMessage('Votre client email va s\'ouvrir. Envoyez le message pour nous contacter !');
    setShowNotification(true);
    
    // Masquer la notification après 5 secondes
    setTimeout(() => {
      setShowNotification(false);
    }, 5000);
    
    // Réinitialiser le formulaire
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      subject: '',
      message: ''
    });
  };

  return (
    <>
      <Notification $show={showNotification} $type={notificationType}>
        {notificationMessage}
      </Notification>
      <SharedPageLayout 
        heroType="contact"
                backgroundImage="/shared_assets/images/contact.jpeg"
        heroTitle={
          <>
            Vos mots <br className="mobile-only" /> pour Outchou
          </>
        }
        heroSubtitle={
          <>
            Vos questions, vos idées et vos partages nourrissent ce projet.
            <br className="desktop-only" />
            Chaque message compte pour faire vivre et transmettre la culturelle algérienne.
          </>
        }
     >
      <ContactMainSection>
        <ContactMainContent>
          <ContactFormSection>
            <FormTitle>Envoyez-nous un message</FormTitle>
            <FormDescription>
              Remplissez le formulaire ci-dessous et nous vous répondrons dans les plus brefs délais.
            </FormDescription>
            
            <ContactForm onSubmit={handleSubmit}>
              <FormRow>
                <FormGroup>
                  <FormLabel htmlFor="firstName">Prénom *</FormLabel>
                  <FormInput 
                    type="text" 
                    id="firstName" 
                    name="firstName" 
                    required 
                    placeholder="Votre prénom"
                    value={formData.firstName}
                    onChange={handleInputChange}
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel htmlFor="lastName">Nom *</FormLabel>
                  <FormInput 
                    type="text" 
                    id="lastName" 
                    name="lastName" 
                    required 
                    placeholder="Votre nom"
                    value={formData.lastName}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </FormRow>

              <FormGroup>
                <FormLabel htmlFor="email">Email *</FormLabel>
                <FormInput 
                  type="email" 
                  id="email" 
                  name="email" 
                  required 
                  placeholder="votre.email@exemple.com"
                  value={formData.email}
                  onChange={handleInputChange}
                />
              </FormGroup>

              <FormGroup>
                <FormLabel htmlFor="subject">Sujet *</FormLabel>
                <FormInput 
                  type="text" 
                  id="subject" 
                  name="subject" 
                  required 
                  placeholder="Sujet de votre message"
                  value={formData.subject}
                  onChange={handleInputChange}
                />
              </FormGroup>

              <FormGroup>
                <FormLabel htmlFor="message">Message *</FormLabel>
                <FormTextarea 
                  id="message" 
                  name="message" 
                  required 
                  placeholder="Votre message..."
                  value={formData.message}
                  onChange={handleInputChange}
                />
              </FormGroup>

              <SubmitButton type="submit">
                Envoyer le message
              </SubmitButton>
            </ContactForm>
          </ContactFormSection>

          <ContactInfoSection>
            <div>
            <ContactTextContent>
              <ContactAccroche>
                La culture algérienne vit grâce à vos échanges.
              </ContactAccroche>
              <ContactInfo>
                <ContactEmail />
              </ContactInfo>
              <SocialSection>
                <SocialMediaLinks hideYouTube={true} />
              </SocialSection>
            </ContactTextContent>
            </div>

            <ContactImage />
          </ContactInfoSection>
        </ContactMainContent>
      </ContactMainSection>
      </SharedPageLayout>
    </>
  );
};

export default ContactPage; 